import React from "react";
import { Box, Divider, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { LEGAL_VERSIONS } from "./legalVersions";

export type LegalDocType =
  | "terms"
  | "privacy"
  | "paidService"
  | "refund"
  | "notice";

const TEXT_MAIN = "#eaf2ff";
const TEXT_SUB = "#9fb4d8";
const SUB_BORDER = "rgba(120,140,180,.14)";

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Typography sx={{ color: TEXT_MAIN, fontWeight: 900, mt: 2, mb: 0.75 }}>
    {children}
  </Typography>
);

const Paragraph = ({ children }: { children: React.ReactNode }) => (
  <Box sx={{ color: TEXT_SUB, fontSize: 13, lineHeight: 1.8, mb: 0.5 }}>
    {children}
  </Box>
);

const InfoBox = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      p: 1.25,
      mt: 1,
      border: `1px solid ${SUB_BORDER}`,
      borderRadius: 2,
      background: alpha("#0e1a2b", 0.5),
      color: TEXT_SUB,
      fontSize: 13,
      lineHeight: 1.7,
    }}
  >
    {children}
  </Box>
);

export function getLegalMeta(type: LegalDocType) {
  switch (type) {
    case "terms":
      return { title: "이용약관", version: LEGAL_VERSIONS.TERMS };
    case "privacy":
      return { title: "개인정보처리방침", version: LEGAL_VERSIONS.PRIVACY };
    case "paidService":
      return { title: "유료서비스 이용약관", version: LEGAL_VERSIONS.PAID_SERVICE };
    case "refund":
      return { title: "구독해지 및 환급정책", version: LEGAL_VERSIONS.REFUND };
    case "notice":
      return { title: "서비스 이용 안내", version: LEGAL_VERSIONS.NOTICE };
    default:
      return { title: "법률 문서", version: "-" };
  }
}

export function LegalDocumentContent({ type }: { type: LegalDocType }) {
  const meta = getLegalMeta(type);

  return (
    <Box>
      <Typography sx={{ color: TEXT_MAIN, fontWeight: 900, fontSize: 18 }}>
        {meta.title}
      </Typography>
      <Typography sx={{ color: TEXT_SUB, mt: 0.5, fontSize: 12 }}>
        시행일: {meta.version}
      </Typography>

      <Divider sx={{ my: 1.5, borderColor: SUB_BORDER }} />

      {type === "terms" && (
        <Box>
          <SectionTitle>제1조 (목적)</SectionTitle>
          <Paragraph>
            본 약관은 풀인(이하 “회사”)이 운영하는 PolicyIntel 서비스(이하 “서비스”)의
            이용조건, 절차, 회사와 이용자의 권리·의무 및 책임사항을 규정함을 목적으로 합니다.
          </Paragraph>

          <SectionTitle>제2조 (서비스의 성격)</SectionTitle>
          <Paragraph>
            본 서비스는 정책, 산업, 공시, 시장 및 관련 데이터를 수집·정리·시각화하여 제공하는
            온라인 정보서비스입니다.
          </Paragraph>
          <Paragraph>
            서비스 내 자료, 분석, 요약, 분류, 점수 및 기타 정보는 일반적 참고자료이며,
            이용자별 투자목적, 재무상황, 위험선호도 등을 반영한 개별 자문을 제공하지 않습니다.
          </Paragraph>

          <SectionTitle>제3조 (회원가입)</SectionTitle>
          <Paragraph>
            이용자는 회사가 정한 가입 절차에 따라 회원정보를 입력하고 필수 약관에 동의함으로써
            회원가입을 신청할 수 있습니다.
          </Paragraph>
          <Paragraph>
            회사는 허위정보 기재, 타인 정보 도용, 서비스 운영 방해 등의 사유가 있는 경우
            가입을 제한하거나 사후 해지할 수 있습니다.
          </Paragraph>

          <SectionTitle>제4조 (서비스의 제공 및 변경)</SectionTitle>
          <Paragraph>
            회사는 서비스의 내용, 구성, 화면, 기능, 제공 방식을 운영상 필요에 따라 변경할 수 있습니다.
          </Paragraph>

          <SectionTitle>제5조 (이용자의 의무)</SectionTitle>
          <Paragraph>
            이용자는 관계 법령, 본 약관, 서비스 내 안내사항을 준수하여야 하며,
            다음 행위를 하여서는 안 됩니다.
          </Paragraph>
          <Paragraph>
            1. 타인 정보 도용 및 허위 정보 등록
            <br />
            2. 서비스 정보의 무단 복제, 배포, 재판매, 자동수집
            <br />
            3. 회사 및 제3자의 권리 침해 행위
            <br />
            4. 서비스 운영을 방해하는 행위
          </Paragraph>

          <SectionTitle>제6조 (면책)</SectionTitle>
          <Paragraph>
            회사는 정보의 정확성, 완전성, 최신성을 높이기 위해 노력하나, 외부 데이터 제공 환경,
            기사 수정, 공시 정정, 시스템 지연 등으로 일부 정보에 오류·누락·시차가 발생할 수 있습니다.
          </Paragraph>
          <Paragraph>
            회사는 법령상 허용되는 범위 내에서 이용자의 판단, 거래, 손익, 기대성과 등에 관하여 보장하지 않으며,
            최종 판단과 책임은 이용자 본인에게 있습니다.
          </Paragraph>
        </Box>
      )}

      {type === "privacy" && (
        <Box>
          <SectionTitle>1. 수집하는 개인정보 항목</SectionTitle>
          <InfoBox>
            <Typography sx={{ color: TEXT_MAIN, fontWeight: 800, mb: 0.5 }}>
              일반 회원가입 필수 항목
            </Typography>
            이메일, 비밀번호

            <Typography sx={{ color: TEXT_MAIN, fontWeight: 800, mt: 1, mb: 0.5 }}>
              소셜 로그인 이용 시
            </Typography>
            Google, NAVER 등 소셜 로그인 제공자의 이용자 식별자와 제공 동의된 이메일 등
            로그인 및 계정 연결에 필요한 최소 정보를 처리할 수 있습니다.
            소셜 로그인으로 가입하는 경우 PolicyIntel용 비밀번호를 별도로 수집하지 않습니다.

            <Typography sx={{ color: TEXT_MAIN, fontWeight: 800, mt: 1, mb: 0.5 }}>
              선택 항목
            </Typography>
            휴대폰 번호, 마케팅 수신 동의 정보
          </InfoBox>

          <SectionTitle>2. 수집 및 이용 목적</SectionTitle>
          <Paragraph>
            회원 식별, 로그인 인증, 소셜 계정 연결 상태 관리, 계정 관리, 서비스 제공,
            고객 문의 대응, 중요 공지 전달, 부정 이용 방지, 유료서비스 운영 및 결제 관련 확인을 위해
            개인정보를 처리합니다.
          </Paragraph>

          <SectionTitle>3. 보유 및 이용기간</SectionTitle>
          <Paragraph>
            회원 탈퇴 시까지 보관합니다. 다만 관계 법령에 따라 보존이 필요한 경우 해당 기간 동안 보관할 수 있습니다.
          </Paragraph>

          <SectionTitle>4. 제3자 제공 및 처리위탁</SectionTitle>
          <Paragraph>
            회사는 원칙적으로 이용자의 개인정보를 외부에 제공하지 않습니다.
            다만 결제, 인증, 고객지원, 클라우드 운영 등 서비스 제공을 위해 필요한 범위에서 처리위탁이 발생할 수 있습니다.
          </Paragraph>
          <Paragraph>
            Google, NAVER 등 소셜 로그인 제공자를 통한 인증 과정에서는 해당 제공자의 서비스가 함께 사용되며,
            회사는 이용자가 동의한 범위에서 로그인 및 계정 연결에 필요한 최소 정보만 전달받아 처리합니다.
          </Paragraph>

          <SectionTitle>5. 이용자의 권리</SectionTitle>
          <Paragraph>
            이용자는 자신의 개인정보에 대해 열람, 정정, 삭제, 처리정지, 동의 철회를 요청할 수 있습니다.
          </Paragraph>

          <SectionTitle>6. 안전성 확보조치</SectionTitle>
          <Paragraph>
            회사는 비밀번호 암호화, 접근권한 관리, 로그기록 보관 등 합리적인 보호조치를 시행합니다.
          </Paragraph>
        </Box>
      )}

      {type === "paidService" && (
        <Box>
          <SectionTitle>제1조 (유료서비스의 내용)</SectionTitle>
          <Paragraph>
            회사는 유료 회원에게 정책·이슈 분석 자료, 정리된 데이터, 추가 기능, 유료 콘텐츠 또는 이에 준하는
            부가 서비스를 제공할 수 있습니다.
          </Paragraph>

          <SectionTitle>제2조 (이용요금 및 결제)</SectionTitle>
          <Paragraph>
            유료서비스의 요금, 과금 주기, 결제수단, 제공 범위는 서비스 화면, 결제 화면 또는 별도 안내 페이지에 표시됩니다.
          </Paragraph>

          <SectionTitle>제3조 (자동결제 및 갱신)</SectionTitle>
          <Paragraph>
            정기결제 상품의 경우 이용자가 동의한 주기마다 자동 갱신될 수 있으며,
            이용자는 서비스 내 설정 또는 고객문의 절차를 통해 해지할 수 있습니다.
          </Paragraph>
          <Paragraph>
            회사는 정기결제 이용자에게 결제 금액, 결제일, 해지 방법 등 주요 내용을 관련 법령 및 운영정책에 따라
            고지할 수 있습니다.
          </Paragraph>

          <SectionTitle>제4조 (이용 제한 및 중단)</SectionTitle>
          <Paragraph>
            회사는 시스템 점검, 장애, 운영상 필요, 약관 위반 등 합리적인 사유가 있는 경우 유료서비스 제공을 제한하거나 중단할 수 있습니다.
          </Paragraph>

          <SectionTitle>제5조 (청약철회, 해지 및 환급)</SectionTitle>
          <Paragraph>
            유료서비스의 청약철회, 구독해지 및 환급은 관계 법령, 결제수단별 정책 및 회사의
            별도 “구독해지 및 환급정책”에 따릅니다.
          </Paragraph>
          <Paragraph>
            월 구독형 정기결제 서비스의 경우 이용자는 다음 결제 예정일 이전까지 구독을 해지할 수 있으며,
            해지 완료 시 다음 결제일부터 추가 청구가 이루어지지 않습니다.
          </Paragraph>

          <SectionTitle>제6조 (주의사항)</SectionTitle>
          <Paragraph>
            유료서비스는 정보 제공 범위의 확대 또는 기능 제공을 의미하며, 특정 결과나 성과를 보장하지 않습니다.
          </Paragraph>
        </Box>
      )}

      {type === "refund" && (
        <Box>
          <SectionTitle>1. 기본 원칙</SectionTitle>
          <Paragraph>
            본 서비스의 유료 결제는 토스페이먼츠를 통한 월 구독 방식의 정기결제를 기준으로 운영됩니다.
            회사는 관계 법령, 결제대행사 정책 및 본 정책에 따라 청약철회, 구독해지 및 환급 절차를 처리합니다.
          </Paragraph>

          <SectionTitle>2. 청약철회 가능 범위</SectionTitle>
          <Paragraph>
            이용자는 결제일로부터 7일 이내에 관련 법령이 정한 범위에서 청약철회를 요청할 수 있습니다.
            다만, 디지털콘텐츠 또는 온라인 서비스의 특성상 결제 후 즉시 서비스 이용이 개시되었거나,
            법령상 청약철회가 제한되는 사유가 있는 경우에는 청약철회가 제한될 수 있습니다.
          </Paragraph>
          <Paragraph>
            회사는 청약철회가 제한되는 경우 그 사실을 결제 화면, 약관 또는 서비스 내 안내 화면 등을 통해
            이용자가 알 수 있도록 표시합니다.
          </Paragraph>

          <SectionTitle>3. 디지털콘텐츠 및 이용 개시 후 환급 제한</SectionTitle>
          <Paragraph>
            본 서비스는 디지털콘텐츠 및 온라인 정보 제공 서비스의 성격을 가지므로,
            회원이 결제 후 즉시 콘텐츠 열람, 데이터 조회, 유료 기능 사용 등 서비스 이용을 개시한 경우에는
            관계 법령이 허용하는 범위에서 환급이 제한될 수 있습니다.
          </Paragraph>
          <Paragraph>
            다만, 회사는 법령상 요구되는 경우 미리보기, 체험 제공, 일부 기능 체험, 정보 고지 등의 방법으로
            이용자가 서비스 성격을 사전에 확인할 수 있도록 운영할 수 있습니다.
          </Paragraph>

          <SectionTitle>4. 구독해지</SectionTitle>
          <Paragraph>
            이용자는 언제든지 서비스 내 계정 설정, 결제 관리 화면 또는 회사가 안내하는 고객문의 절차를 통해
            구독해지를 신청할 수 있습니다.
          </Paragraph>
          <Paragraph>
            구독해지가 완료되면 다음 결제 예정일부터 자동결제가 중단되며,
            이미 결제가 완료된 이용기간 동안에는 특별한 사정이 없는 한 서비스 이용 권한이 유지됩니다.
          </Paragraph>

          <SectionTitle>5. 중도해지와 환급 처리</SectionTitle>
          <Paragraph>
            월 구독 서비스의 특성상 단순 변심, 이용 빈도 부족, 개인 사정 등만을 이유로 한 중도해지의 경우,
            이미 개시된 당월 이용기간에 대한 환급은 제한될 수 있습니다.
          </Paragraph>
          <Paragraph>
            다만, 관계 법령상 환급이 필요한 경우, 회사의 귀책사유로 정상적인 서비스 제공이 현저히 불가능한 경우,
            또는 결제 오류·중복결제 등 명백한 결제 이상이 확인되는 경우에는 전부 또는 일부 환급이 이루어질 수 있습니다.
          </Paragraph>

          <SectionTitle>6. 환급 기준 및 방법</SectionTitle>
          <Paragraph>
            환급이 필요한 경우 회사는 관련 법령, 결제수단별 취소 기준 및 결제대행사 정책에 따라 처리합니다.
          </Paragraph>
          <Paragraph>
            이용 개시 후 환급이 예외적으로 인정되는 경우에는 이용일수, 이용내역, 제공된 혜택,
            결제수수료 및 결제수단별 처리 기준 등을 고려하여 합리적인 범위에서 환급액이 산정될 수 있습니다.
          </Paragraph>

          <SectionTitle>7. 결제 오류 및 중복결제</SectionTitle>
          <Paragraph>
            동일 결제의 중복 승인, 시스템 오류에 따른 오결제, 승인 금액 착오 등 명백한 결제 이상이 확인되는 경우,
            회사는 확인 절차를 거쳐 해당 금액의 전부 또는 차액을 취소 또는 환급 처리합니다.
          </Paragraph>

          <SectionTitle>8. 문의 및 처리 절차</SectionTitle>
          <Paragraph>
            구독해지, 청약철회, 결제내역 확인, 중복결제, 환급 문의는 서비스 내 고객문의 또는 회사가 고지한 이메일을 통해 접수할 수 있습니다.
          </Paragraph>
          <Paragraph>
            회사는 접수된 요청에 대해 결제기록, 이용내역, 접수시점, 장애 여부 등을 확인한 후 관련 법령과 정책에 따라 처리합니다.
          </Paragraph>
        </Box>
      )}

      {type === "notice" && (
        <Box>
          <SectionTitle>1. 서비스 이용 안내</SectionTitle>
          <Paragraph>
            본 서비스는 정책, 산업, 공시 및 시장 관련 정보를 정리·분석하여 제공하는 참고용 정보서비스입니다.
          </Paragraph>

          <SectionTitle>2. 자료의 한계</SectionTitle>
          <Paragraph>
            서비스에 포함된 요약, 분류, 점수, 시각화, 과거 데이터 비교 및 기타 정보는 데이터 수집 시점,
            외부 제공처 환경, 정정 여부 등에 따라 달라질 수 있습니다.
          </Paragraph>

          <SectionTitle>3. 이용자 확인 필요</SectionTitle>
          <Paragraph>
            중요한 판단 전에는 공시, 원문 기사, 정책 자료 등 원자료를 추가 확인하시기 바랍니다.
          </Paragraph>

          <SectionTitle>4. 책임 범위</SectionTitle>
          <Paragraph>
            회사는 특정 결과, 수익, 성과 또는 판단의 적합성을 보장하지 않습니다.
          </Paragraph>
        </Box>
      )}
    </Box>
  );
}
